<template>
  <div class="flex flex-col h-full overflow-hidden">
    <ThreadsNav show-back :go-back="goBack" :show-center="false">
      <template #right>
        <button class="btn btn-primary btn-sm" @click="onSave">
          {{ $t("Buttons.save") }}
        </button>
      </template>
    </ThreadsNav>
    <div
      v-if="loading"
      class="flex gap-2 w-full h-full p-2 items-center justify-center flex-1"
    >
      <div class="loading loading-ring"></div>
    </div>
    <template v-else>
      <div class="ml-4 flex flex-col">
        <template v-if="!props.hideCustomTone">
          <div class="red-dot mb-2 font-bold">
            {{ $t(`User.mate.tone.current`) }}
          </div>

          <div class="text-base-content/50" v-if="!activeTone">
            {{ $t(`game.show.unselected`) }}
          </div>
          <template v-else>
            <div class="flex items-center font-semibold mb-2 flex-wrap">
              {{ activeTone.name }}

              <span
                v-if="
                  state.mateToneConfig.speedRatio &&
                  state.mateToneConfig.speedRatio !== 1
                "
              >
                ({{ $t(`User.mate.tone.speed`) }}x{{
                  state.mateToneConfig.speedRatio
                }})
              </span>
              <template v-if="state.mateToneConfig.emotion">
                <span>(</span>
                <span>
                  {{
                    $t(`User.mate.tone.emotion.${state.mateToneConfig.emotion}`)
                  }}
                </span>
                <span
                  v-if="
                    state.mateToneConfig.emotionScale &&
                    state.mateToneConfig.emotionScale !== 1
                  "
                >
                  {{
                    $t(`User.mate.tone.value`) +
                    ": " +
                    state.mateToneConfig.emotionScale
                  }}
                </span>
                <span>)</span>
              </template>
            </div>

            <div class="flex items-center gap-1">
              <button class="btn btn-xs" @click="toggleTone(activeTone)">
                <div
                  v-if="activeTone.media"
                  class="w-6 flex items-center justify-center"
                >
                  <i
                    v-if="state.audioStatus === 'playing'"
                    class="iconify mingcute--pause-circle-line"
                  />
                  <i
                    v-else-if="state.audioStatus === 'loading'"
                    class="loading loading-dots loading-xs"
                  />
                  <i
                    v-else
                    class="iconify mingcute--play-circle-line fill-base-content"
                  />
                </div>
                {{ $t(`Buttons.tryListen`) }}
              </button>
              <button class="btn btn-xs" @click="state.showCustomTone = true">
                <i class="iconify mingcute--edit-2-line"></i>
                {{ $t(`User.mate.tone.customTryListen`) }}
              </button>
              <button class="btn btn-xs" @click="state.showVoiceSpeed = true">
                <i class="iconify mingcute--settings-2-line"></i>
                {{ $t(`User.mate.tone.speed`) }}
              </button>
              <button
                v-if="activeTone.category.name === '多情感'"
                class="btn btn-xs"
                @click="state.showEmotion = true"
              >
                <i class="iconify mingcute--settings-2-line"></i>
                {{ $t(`User.mate.tone.emotionTitle`) }}
              </button>
            </div>
          </template>
        </template>

        <div class="red-dot mt-3 flex">音色库</div>

        <VanTabs v-model:active="state.tab" class="-ml-5">
          <VanTab name="0" :title="$t('Buttons.all')" />
          <VanTab
            v-for="item in toneStore.toneCategoryList"
            :key="item.id"
            :name="item.id.toString()"
            :title="item.name"
          />
        </VanTabs>

        <boxed-tab
          class="mt-1"
          :selected-idx="activeSortIdx"
          @update:selected-idx="onSorterChange"
        >
          <div v-for="item in ['全部', '男', '女']" :key="item">
            {{ item }}
          </div>
        </boxed-tab>
      </div>
      <div class="flex-1 overflow-auto p-2 gap-1 flex flex-col">
        <div class="flex justify-between items-center border p-2 rounded">
          <div class="flex gap-2 items-center">
            <i class="iconify mingcute--close-circle-line" />
            {{ $t("Buttons.notSetTone") }}
          </div>
          <div v-if="state.toneId === 0" class="btn btn-ghost btn-sm">
            <i class="iconify mingcute--check-circle-line text-success" />
            {{ $t("Buttons.selected") }}
          </div>
          <button v-else class="btn btn-sm rounded" @click="clickSelectTone(0)">
            <magic-stick-icon class="icon-sm" />
            {{ $t("Buttons.select") }}
          </button>
        </div>
        <div
          v-for="tone in toneList"
          :key="tone.id"
          class="flex justify-between items-center border p-2 rounded"
        >
          <div>
            <div class="flex gap-2 items-center flex-1 pr-2">
              <div
                v-if="tone.media"
                class="w-6 flex items-center justify-center"
              >
                <i
                  v-if="
                    state.toneInAudio?.id === tone.id &&
                    state.audioStatus === 'playing'
                  "
                  class="iconify mingcute--pause-circle-line"
                  @click="audioPlayer?.pause()"
                />
                <i
                  v-else-if="
                    state.toneInAudio?.id === tone.id &&
                    state.audioStatus === 'loading'
                  "
                  class="loading loading-dots loading-xs"
                />
                <i
                  v-else
                  :class="{ 'fill-base-content': tone.id === state.toneId }"
                  class="iconify mingcute--play-circle-line"
                  @click="onPlay(tone)"
                />
              </div>
              <span class="flex-1">
                {{ tone.name }}
              </span>
            </div>

            <div class="flex gap-1 mt-1">
              <VanTag>
                {{
                  tone.gender === 1
                    ? $t("Newmates.male")
                    : $t("Newmates.female")
                }}
              </VanTag>

              <VanTag>{{ tone.category.name }}</VanTag>
            </div>
          </div>
          <div v-if="tone.id === state.toneId" class="btn btn-ghost btn-sm">
            <i class="iconify mingcute--check-circle-line text-success" />
            {{ $t("Buttons.selected") }}
          </div>
          <button
            v-else
            class="btn btn-sm rounded"
            @click="clickSelectTone(tone.id)"
          >
            <magic-stick-icon class="icon-sm" />
            {{ $t("Buttons.select") }}
          </button>
        </div>
      </div>
    </template>

    <audio
      ref="audioPlayer"
      :src="state.toneInAudio?.media.url"
      class="hidden"
      controls
      @ended="state.audioStatus = 'ended'"
      @loadstart="state.audioStatus = 'loading'"
      @pause="state.audioStatus = 'paused'"
      @playing="state.audioStatus = 'playing'"
      crossorigin="anonymous"
    />

    <MateToneModalModule
      v-model:show-custom-tone="state.showCustomTone"
      :mate-tone-id="state.toneId"
      v-model:show-voice-speed="state.showVoiceSpeed"
      v-model:show-emotion="state.showEmotion"
      :mate-tone-config="state.mateToneConfig"
    />
  </div>
</template>

<script setup lang="ts">
import MagicStickIcon from "@/components/icon/MagicStickIcon.vue";
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from "vue";
import { useTone } from "@/stores/tone";
import { MateTone, MateToneConfig } from "@/types/entity";
import { useMate } from "@/stores/mate";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { useRoute } from "vue-router";
import { routerBack } from "@/use/go";

// 常量
// props & emit

// 内部state
const audioPlayer = ref<HTMLAudioElement | null>(null);

const props = defineProps<{
  hideCustomTone?: boolean;
  mateUuid?: string;
}>();

const emit = defineEmits<{
  (e: "save"): void;
}>();

const route = useRoute();
const mateUuid = ref(props.mateUuid ?? route.query.mateUuid);
const mateStore = useMate();
const toneStore = useTone();
const loading = ref(false);
const state = reactive({
  toneInAudio: null as MateTone | null,
  audioStatus: "ended", // loading, playing, paused, ended
  isPlaying: false,
  gender: undefined as number | undefined,
  toneId: 0,
  tab: "0",

  mateToneConfig: {} as MateToneConfig,

  showCustomTone: false,

  showVoiceSpeed: false,
  showEmotion: false,
});

const defaultMateToneConfig = () => ({
  speedRatio: 1,
  emotion: undefined,
  emotionScale: 4,
});

const goBack = async (isSaved: boolean = false) => {
  if (!isSaved) {
    const mate = await mateStore.getOrFetchMate(mateUuid.value as string);
    if (
      mate.mateToneId !== state.toneId ||
      JSON.stringify(mate.mateToneConfig) !==
        JSON.stringify(state.mateToneConfig)
    ) {
      // 提示
      try {
        await showConfirmDialog({
          title: "提示",
          message: "是否保存当前音色配置？",
          confirmButtonText: "保存",
          cancelButtonText: "不保存",
        });
        await onSave();
      } catch {
        console.log("不保存");
      }
    }
  }

  if (props.hideCustomTone) {
    emit("save");
  } else {
    routerBack();
  }
};

const clickSelectTone = (toneId: number) => {
  if (toneId != state.toneId) {
    state.toneId = toneId;
    state.mateToneConfig = defaultMateToneConfig();
  }
};
const activeTone = computed(() => {
  if (state.toneId === 0) return null;
  return toneStore.toneMap[state.toneId];
});

const toneList = computed(() => {
  if (state.tab === "0" && state.gender === undefined) {
    return toneStore.list;
  }

  let { list } = toneStore;

  if (state.gender !== undefined) {
    list = list.filter((item) => item.gender === state.gender);
  }

  if (state.tab !== "0") {
    list = list.filter((item) => item.categoryId === Number(state.tab));
  }

  return list;
});

onBeforeUnmount(() => {
  audioPlayer.value?.pause();
});

// 前置请求
onBeforeMount(async () => {
  try {
    loading.value = true;
    await Promise.all([
      toneStore.getToneList(),
      toneStore.getToneCategoryList(),
    ]);
  } finally {
    loading.value = false;
  }
});
// 事件回调
const onPlay = async (tone: MateTone, speedRatio: number = 1) => {
  state.toneInAudio = tone;

  await audioPlayer.value!.load();
  await audioPlayer.value!.play();
  audioPlayer.value!.playbackRate = speedRatio;
};

const toggleTone = (tone: MateTone) => {
  if (audioPlayer.value?.paused) {
    onPlay(tone, state.mateToneConfig.speedRatio);
  } else {
    audioPlayer.value?.pause();
  }
};

const onSave = async () => {
  const loading = showLoadingToast({
    message: "保存中...",
  });
  if (!state.toneId) {
    mateStore.updateMateDetail(mateUuid.value as string, {
      mateToneId: 0,
      mateToneConfig: defaultMateToneConfig(),
    });
  } else {
    mateStore.updateMateDetail(mateUuid.value as string, {
      mateToneId: state.toneId,
      mateToneConfig: state.mateToneConfig,
    });
  }
  loading.close();
  showSuccessToast("保存成功");
  goBack(true);
};

const activeSortIdx = ref(0);
const onSorterChange = (idx: number) => {
  if (idx === 0) state.gender = undefined;
  else if (idx === 1) state.gender = 1;
  else state.gender = 0;
  activeSortIdx.value = idx;
};

onMounted(async () => {
  const mate = await mateStore.getOrFetchMate(mateUuid.value as string);
  if (mate.mateToneId) {
    state.toneId = mate.mateToneId;
  }
  if (mate.mateToneConfig) {
    state.mateToneConfig = { ...mate.mateToneConfig };
  }
});
</script>
